import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  Divider
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  configCard: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));

const LiveCallsConfig = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);

  // Form fields
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [apiKeySid, setApiKeySid] = useState("");
  const [apiKeySecret, setApiKeySecret] = useState("");
  const [applicationSid, setApplicationSid] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [enabled, setEnabled] = useState(true);

  // Status
  const [configStatus, setConfigStatus] = useState(null);

  const companyId = user?.companyId;

  useEffect(() => {
    // Solo cargar config cuando companyId esté disponible
    if (companyId) {
      loadConfig();
    } else {
      // Si no hay companyId, marcar como no loading
      setLoading(false);
    }
  }, [companyId]); // Agregar companyId como dependencia

  const loadConfig = async () => {
    // Validación adicional por seguridad
    if (!companyId) {
      console.warn("Company ID not available, skipping config load");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.get("/api/twilio/live-calls/config", {
        params: { companyId }
      });

      if (data.configured && data.config) {
        setConfigured(true);
        setAccountSid(data.config.accountSid || "");
        // Don't set masked values
        setApiKeySid(data.config.apiKeySid || "");
        setApplicationSid(data.config.applicationSid || "");
        setPhoneNumber(data.config.phoneNumber || "");
        setEnabled(data.config.enabled !== false);
      } else {
        // No configuration exists yet - this is normal for first-time setup
        setConfigured(false);
      }
    } catch (error) {
      console.error("Error loading config:", error);
      // Only show error if it's a real error (not just missing config)
      if (error.response && error.response.status !== 404) {
        toast.error("Error al cargar la configuración");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!companyId) {
      toast.error("Company ID no disponible. Por favor, inicia sesión nuevamente.");
      return;
    }

    try {
      // Validate required fields
      if (!accountSid || !authToken || !phoneNumber) {
        toast.error("Account SID, Auth Token y Número de Teléfono son requeridos");
        return;
      }

      setSaving(true);
      setConfigStatus(null);

      const payload = {
        companyId: parseInt(companyId, 10),
        accountSid,
        authToken,
        phoneNumber,
        enabled
      };

      // Only include optional fields if they have values
      if (apiKeySid) payload.apiKeySid = apiKeySid;
      if (apiKeySecret) payload.apiKeySecret = apiKeySecret;
      if (applicationSid) payload.applicationSid = applicationSid;

      const { data } = await api.post("/api/twilio/live-calls/config", payload);

      if (data.success) {
        toast.success("Configuración guardada exitosamente");
        setConfigured(true);
        setConfigStatus({
          success: true,
          message: "Configuración guardada correctamente"
        });
        // Clear sensitive fields after save
        setAuthToken("");
        setApiKeySecret("");
      }
    } catch (error) {
      console.error("Error saving config:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar la configuración";
      toast.error(errorMessage);
      setConfigStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!companyId) {
      toast.error("Company ID no disponible. Por favor, inicia sesión nuevamente.");
      return;
    }

    try {
      const { data } = await api.get("/api/twilio/live-calls/config/status", {
        params: { companyId }
      });

      if (data.configured) {
        setConfigStatus({
          success: true,
          message: "Llamadas en Vivo está configurado correctamente"
        });
        toast.success("Configuración verificada");
      } else {
        setConfigStatus({
          success: false,
          message: "Llamadas en Vivo no está configurado"
        });
        toast.warning("Configuración incompleta");
      }
    } catch (error) {
      console.error("Error checking status:", error);
      toast.error("Error al verificar el estado");
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Configuración de Llamadas en Vivo</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  if (!companyId) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Configuración de Llamadas en Vivo</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            {loading ? (
              <CircularProgress />
            ) : (
              <Typography variant="h6" color="error">
                Cargando información de la empresa... Si este mensaje persiste, por favor inicia sesión nuevamente.
              </Typography>
            )}
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Configuración de Llamadas en Vivo</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCheckStatus}
            startIcon={<RefreshIcon />}
            style={{ marginRight: 8 }}
          >
            Verificar Estado
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Guardar Configuración
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.configCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Credenciales de Twilio
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Configura tus credenciales de Twilio para habilitar las llamadas en vivo desde el navegador.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Account SID *"
                  value={accountSid}
                  onChange={(e) => setAccountSid(e.target.value)}
                  className={classes.formField}
                  helperText="Tu Account SID de Twilio"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Auth Token *"
                  value={authToken}
                  onChange={(e) => setAuthToken(e.target.value)}
                  type="password"
                  className={classes.formField}
                  helperText="Tu Auth Token de Twilio (se guardará encriptado)"
                  placeholder={configured ? "••••••••••••••••" : ""}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Número de Teléfono *"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={classes.formField}
                  helperText="Tu número de Twilio (formato: +1234567890)"
                  placeholder="+1234567890"
                />

                <Divider style={{ margin: "24px 0" }} />

                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Configuración Avanzada (Opcional)
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Para mayor seguridad en producción, se recomienda usar API Keys en lugar de Auth Token.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="API Key SID (Opcional)"
                  value={apiKeySid}
                  onChange={(e) => setApiKeySid(e.target.value)}
                  className={classes.formField}
                  helperText="API Key SID para mayor seguridad (recomendado para producción)"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="API Key Secret (Opcional)"
                  value={apiKeySecret}
                  onChange={(e) => setApiKeySecret(e.target.value)}
                  type="password"
                  className={classes.formField}
                  helperText="API Key Secret (se guardará encriptado)"
                  placeholder={configured && apiKeySid ? "••••••••••••••••" : ""}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Application SID (Opcional)"
                  value={applicationSid}
                  onChange={(e) => setApplicationSid(e.target.value)}
                  className={classes.formField}
                  helperText="TwiML Application SID para configuración personalizada"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Habilitar Llamadas en Vivo"
                />

                {configStatus && (
                  <Box
                    className={`${classes.statusBox} ${
                      configStatus.success
                        ? classes.statusSuccess
                        : classes.statusError
                    }`}
                  >
                    {configStatus.success ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )}
                    <Typography variant="body2">
                      {configStatus.message}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instrucciones de Configuración
                </Typography>
                <Typography variant="body2" paragraph>
                  Para configurar las Llamadas en Vivo, necesitas obtener tus credenciales de Twilio:
                </Typography>

                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  1. Obtener Account SID y Auth Token
                </Typography>
                <Typography variant="body2" paragraph>
                  • Ve a tu <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer">Consola de Twilio</a>
                  <br />
                  • Copia tu Account SID y Auth Token
                </Typography>

                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  2. Obtener Número de Teléfono
                </Typography>
                <Typography variant="body2" paragraph>
                  • Ve a <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming" target="_blank" rel="noopener noreferrer">Phone Numbers</a>
                  <br />
                  • Copia tu número activo (debe tener capacidad de voz)
                </Typography>

                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  3. Crear API Key (Recomendado para Producción)
                </Typography>
                <Typography variant="body2" paragraph>
                  • Ve a <a href="https://console.twilio.com/us1/account/keys-credentials/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a>
                  <br />
                  • Crea una nueva API Key
                  <br />
                  • Guarda el SID y Secret (solo se muestra una vez)
                </Typography>

                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  4. Crear TwiML Application (Opcional)
                </Typography>
                <Typography variant="body2" paragraph>
                  • Ve a <a href="https://console.twilio.com/us1/develop/voice/manage/twiml-apps" target="_blank" rel="noopener noreferrer">TwiML Apps</a>
                  <br />
                  • Crea una nueva aplicación
                  <br />
                  • Configura la Voice URL: <code>{window.location.origin}/api/twilio/live-calls/twiml</code>
                  <br />
                  • Copia el Application SID
                </Typography>

                <Divider style={{ margin: "16px 0" }} />

                <Typography variant="subtitle2" gutterBottom>
                  Notas Importantes
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Los campos marcados con * son obligatorios
                  <br />
                  • Las credenciales se guardan de forma segura en la base de datos
                  <br />
                  • Se recomienda usar API Keys en lugar de Auth Token para producción
                  <br />
                  • El Application SID es opcional pero recomendado para configuración avanzada
                </Typography>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ¿Cómo usar las Llamadas en Vivo?
                </Typography>
                <Typography variant="body2" paragraph>
                  Una vez configurado, podrás:
                </Typography>
                <Typography variant="body2" component="div">
                  • Hacer llamadas directamente desde tu navegador
                  <br />
                  • Llamar a cualquier número de teléfono
                  <br />
                  • Controlar el volumen y silenciar el micrófono
                  <br />
                  • Ver el historial de llamadas
                  <br />
                  • Exportar el historial a CSV
                </Typography>
                <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
                  Ve a <strong>Llamadas en Vivo</strong> en el menú principal para comenzar a hacer llamadas.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default LiveCallsConfig;
